/**
 * The tailor's wording, in the three languages a Rajkot workshop reads.
 *
 * <p>
 * <strong>⚠ THE GUJARATI AND HINDI ARE DRAFTS AND HAVE NOT BEEN REVIEWED BY A TAILOR.</strong>
 * They were written by the machine that wrote this file. Tailoring verbs are specialist — "let out
 * at the side seams" has a right and a wrong translation and the wrong one ruins a garment — so
 * every sheet prints a pending-review marker until somebody who knows the trade has read this file
 * and {@link translationsReviewed} is set to true. Do not remove that marker to make a docket look
 * finished.
 * </p>
 *
 * <p>
 * The English is <strong>not</strong> duplicated here: it stays in `alterationNarrative.ts`, which
 * is the file its tests already pin. Copying it would make two places to change a verb and one of
 * them would be missed.
 * </p>
 *
 * <p>
 * Laid out with the English on each line so this file is reviewable by a person who does not read
 * TypeScript. To correct a phrase, change the string — nothing else needs touching.
 * </p>
 */

import type { DocketLanguage as Language } from '../generated/enums';

/*
  Re-exported so callers have one place to reach for the language, and it is the *generated* enum —
  the server's own vocabulary, kept in step by the codegen test. An earlier version of this file
  declared its own 'en' | 'gu' | 'hi' beside it, which is exactly the two-vocabularies problem this
  codebase avoids everywhere else.
*/
export { DocketLanguageValues, type DocketLanguage } from '../generated/enums';

/**
 * Flip to true once a tailor or the shop owner has read the wording below.
 *
 * <p>
 * Until then the docket prints a line saying so. A wrong verb here is not a typo — it is a garment
 * cut the wrong way — and a sheet that looks authoritative while carrying unreviewed instructions
 * is worse than one that admits what it is.
 * </p>
 */
export const translationsReviewed = false;

/** What each language calls itself, for the label above its block on the sheet. */
export const languageNames: Record<Language, string> = {
  English: 'English',
  Gujarati: 'ગુજરાતી',
  Hindi: 'हिन्दी',
};

/**
 * The BCP-47 tag for the `lang` attribute.
 *
 * <p>
 * Separate from the enum on purpose: the enum is the shop's own vocabulary and the tag is the
 * web's. A screen reader and a hyphenation engine both need the tag, and neither has heard of
 * "Gujarati" as an identifier.
 * </p>
 */
export const languageTags: Record<Language, string> = {
  English: 'en',
  Gujarati: 'gu',
  Hindi: 'hi',
};

export interface MeasureVerbs {
  /** What to call it: "both sleeves", "the waist". */
  subject: string;
  /** Verb for a negative delta — taking away. */
  decrease: string;
  /** Verb for a positive delta — adding. */
  increase: string;
  /** Where on the garment, appended when present. */
  where?: string;
}

/** The sentence furniture that is not a measure: units, and the fallback verbs. */
export interface NarrativePhrases {
  /** Singular and plural of "inch". Gujarati and Hindi do not inflect it the way English does. */
  inch: string;
  inches: string;
  /** Used when the catalogue has a measure this file has no wording for. */
  reduce: string;
  increase: string;
  /** Joins the parts: English is "Shorten X by N inches", the Indic languages put the verb last. */
  order: 'verb-first' | 'verb-last';
  /** How a note in parentheses is introduced, if at all. */
  by: string;
}

/* -------------------------------------------------------------------- ગુજરાતી */

/**
 * Gujarati.
 *
 * Verb-last, as the language is. "બંને બાઈ કફ થી ૧½ ઇંચ ટૂંકી કરો" reads
 * *both sleeves / from the cuff / 1½ inch / shorten*, which is the order a Gujarati speaker
 * expects — the English order transliterated would be understood but reads as a foreigner talking.
 */
const gujarati: Record<string, MeasureVerbs> = {
  // Sleeve — both sleeves / shorten / lengthen / from the cuff, keeping the existing buttons
  Sleeve: {
    subject: 'બંને બાઈ',
    decrease: 'ટૂંકી કરો',
    increase: 'લાંબી કરો',
    where: 'કફ થી, હાલના બટનની જગ્યા એ જ રાખો',
  },

  // Shoulder — the shop's word સોલ્ડર / narrow / widen
  Shoulder: { subject: 'સોલ્ડર', decrease: 'સાંકડું કરો', increase: 'પહોળું કરો' },

  // Chest — the chest / take in / let out / at the seams
  Chest: { subject: 'છાતી', decrease: 'ઓછી કરો', increase: 'વધારો', where: 'સિલાઈ પરથી' },

  // Waist — the waist / take in / let out / at the side seams
  Waist: { subject: 'કમર', decrease: 'ઓછી કરો', increase: 'વધારો', where: 'બાજુની સિલાઈ પરથી' },

  // Length — the overall length / shorten / lengthen
  Length: { subject: 'લંબાઈ', decrease: 'ટૂંકી કરો', increase: 'લાંબી કરો' },

  // TrouserWaist — the trouser waist / take in / let out
  TrouserWaist: { subject: 'પેન્ટની કમર', decrease: 'ઓછી કરો', increase: 'વધારો' },

  // TrouserHem — the trouser hem / raise / lower / finish plain, no turn-up
  TrouserHem: {
    subject: 'પેન્ટનો ઘેર',
    decrease: 'ઊંચો કરો',
    increase: 'નીચો કરો',
    where: 'સાદો રાખો, મોડ ન કરો',
  },

  // Neck — the neck / take in / let out
  Neck: { subject: 'ગળું', decrease: 'ઓછું કરો', increase: 'વધારો' },

  // The shop's own card (2026-10-17): subjects are the words its boxes abbreviate.
  // Pet (પે.) — the pet / take in / let out
  Pet: { subject: 'પેટ', decrease: 'ઓછું કરો', increase: 'વધારો' },

  // Seat (સી., upper) — the seat / take in / let out
  Seat: { subject: 'સીટ', decrease: 'ઓછી કરો', increase: 'વધારો' },

  // Collar (કો.) — the collar / take in / let out
  Collar: { subject: 'કોલર', decrease: 'ઓછો કરો', increase: 'વધારો' },

  // TrouserLength (લં., lower) — the trouser length / shorten / lengthen
  TrouserLength: { subject: 'પેન્ટની લંબાઈ', decrease: 'ટૂંકી કરો', increase: 'લાંબી કરો' },

  // TrouserSeat (સી., lower) — the trouser seat / take in / let out
  TrouserSeat: { subject: 'પેન્ટની સીટ', decrease: 'ઓછી કરો', increase: 'વધારો' },

  // Thigh (જા.) — the thigh / take in / let out
  Thigh: { subject: 'જાંઘ', decrease: 'ઓછી કરો', increase: 'વધારો' },

  // Joro (જો.) — the shop's word, kept as it is / reduce / increase
  Joro: { subject: 'જોરો', decrease: 'ઓછો કરો', increase: 'વધારો' },

  // Moriyu (મો.) — the bottom opening, મોરિયું in the shop's word / narrow / widen
  Moriyu: { subject: 'મોરિયું', decrease: 'ઓછું કરો', increase: 'વધારો' },

  // Golai (ગો.) — the shop's word, kept as it is / reduce / increase
  Golai: { subject: 'ગોળાઈ', decrease: 'ઓછી કરો', increase: 'વધારો' },
};

const gujaratiPhrases: NarrativePhrases = {
  inch: 'ઇંચ', // inch
  inches: 'ઇંચ', // inches — Gujarati does not inflect it here
  reduce: 'ઓછું કરો', // reduce
  increase: 'વધારો', // increase
  order: 'verb-last',
  by: '', // the amount stands on its own before the verb
};

/* --------------------------------------------------------------------- हिन्दी */

/**
 * Hindi.
 *
 * Also verb-last. "दोनों आस्तीन कफ से १½ इंच छोटी करें" — *both sleeves / from the cuff /
 * 1½ inch / shorten*. The imperative is the polite -ें form, which is what a shop uses with a
 * tailor it works with rather than the blunt -ो.
 */
const hindi: Record<string, MeasureVerbs> = {
  // Sleeve — both sleeves / shorten / lengthen / from the cuff, keeping the existing buttons
  Sleeve: {
    subject: 'दोनों आस्तीन',
    decrease: 'छोटी करें',
    increase: 'लंबी करें',
    where: 'कफ से, बटन की मौजूदा जगह वैसी ही रखें',
  },

  // Shoulder — the shoulders / narrow / widen
  Shoulder: { subject: 'कंधे', decrease: 'सँकरे करें', increase: 'चौड़े करें' },

  // Chest — the chest / take in / let out / at the seams
  Chest: { subject: 'छाती', decrease: 'कम करें', increase: 'बढ़ाएँ', where: 'सिलाई से' },

  // Waist — the waist / take in / let out / at the side seams
  Waist: { subject: 'कमर', decrease: 'कम करें', increase: 'बढ़ाएँ', where: 'बगल की सिलाई से' },

  // Length — the overall length / shorten / lengthen
  Length: { subject: 'कुल लंबाई', decrease: 'छोटी करें', increase: 'लंबी करें' },

  // TrouserWaist — the trouser waist / take in / let out
  TrouserWaist: { subject: 'पैंट की कमर', decrease: 'कम करें', increase: 'बढ़ाएँ' },

  // TrouserHem — the trouser hem / raise / lower / finish plain, no turn-up
  TrouserHem: {
    subject: 'पैंट का घेरा',
    decrease: 'ऊपर करें',
    increase: 'नीचे करें',
    where: 'सादा रखें, मोड़ न करें',
  },

  // Neck — the neck / take in / let out
  Neck: { subject: 'गला', decrease: 'कम करें', increase: 'बढ़ाएँ' },

  // The shop's own card (2026-10-17).
  // Pet — the pet / take in / let out
  Pet: { subject: 'पेट', decrease: 'कम करें', increase: 'बढ़ाएँ' },

  // Seat (upper) — the seat / take in / let out
  Seat: { subject: 'सीट', decrease: 'कम करें', increase: 'बढ़ाएँ' },

  // Collar — the collar / take in / let out
  Collar: { subject: 'कॉलर', decrease: 'कम करें', increase: 'बढ़ाएँ' },

  // TrouserLength — the trouser length / shorten / lengthen
  TrouserLength: { subject: 'पैंट की लंबाई', decrease: 'छोटी करें', increase: 'लंबी करें' },

  // TrouserSeat — the trouser seat / take in / let out
  TrouserSeat: { subject: 'पैंट की सीट', decrease: 'कम करें', increase: 'बढ़ाएँ' },

  // Thigh — the thigh / take in / let out
  Thigh: { subject: 'जांघ', decrease: 'कम करें', increase: 'बढ़ाएँ' },

  // Joro — the shop's word, kept as it is / reduce / increase
  Joro: { subject: 'जोरो', decrease: 'कम करें', increase: 'बढ़ाएँ' },

  // Moriyu — the bottom opening / narrow / widen
  Moriyu: { subject: 'मोरियू', decrease: 'कम करें', increase: 'बढ़ाएँ' },

  // Golai — the shop's word, kept as it is / reduce / increase
  Golai: { subject: 'गोलाई', decrease: 'कम करें', increase: 'बढ़ाएँ' },
};

const hindiPhrases: NarrativePhrases = {
  inch: 'इंच', // inch
  inches: 'इंच', // inches — Hindi does not inflect it here
  reduce: 'कम करें', // reduce
  increase: 'बढ़ाएँ', // increase
  order: 'verb-last',
  by: '',
};

/* -------------------------------------------------------------------- English */

const englishPhrases: NarrativePhrases = {
  inch: 'inch',
  inches: 'inches',
  reduce: 'Reduce',
  increase: 'Increase',
  order: 'verb-first',
  by: 'by ',
};

/**
 * The wording for one language.
 *
 * <p>
 * English returns an empty measure map on purpose: `alterationNarrative` keeps its own, which its
 * tests pin, and a second copy here would be a second place to change a verb.
 * </p>
 */
export function phrasesFor(language: Language): NarrativePhrases {
  switch (language) {
    case 'Gujarati':
      return gujaratiPhrases;
    case 'Hindi':
      return hindiPhrases;
    default:
      return englishPhrases;
  }
}

export function measuresFor(language: Language): Record<string, MeasureVerbs> {
  switch (language) {
    case 'Gujarati':
      return gujarati;
    case 'Hindi':
      return hindi;
    default:
      return {};
  }
}
